/**
 * Re-login recovery for a checkout session fetch that returns 401 (CXO-3293).
 *
 * A 401 on the first checkout/v3 call means the auth token never made it to
 * PPUI (handoff loss) or has expired. Retrying doesn't help — in prod the old
 * 1s-sleep retry recovered 7 of ~21k sessions a day — so instead we send the
 * shopper back through Tamara Auth for this checkout, which sets a fresh token
 * and returns them here.
 *
 * Loop guard: the Auth return URL carries `reauth=1`. If a session that just
 * came back from re-login still 401s, we stop and show the error screen rather
 * than redirecting again.
 *
 * Inside the Tamara app (Mobile bridge present) the app owns login, so we never
 * send its WebView to web Auth. There the 401 is a handoff race (CXO-3578): the
 * app writes the token cookie and loads us straight away, and on iOS the
 * WKWebView cookie store has not caught up by our first request. A single
 * reload of the same URL picks the cookie up, so we reload once (same
 * `reauth=1` loop guard) instead of showing a dead error screen.
 */

import { getResolvedCheckoutId, isValidCheckoutId } from "@/lib/checkout-id";
import { isMobileBridgeAvailable } from "@/lib/mobile";
import {
  REAUTH_MARKER_PARAM,
  REAUTH_MARKER_VALUE,
  buildAuthLoginUrl,
} from "@/modules/handshake/lib/build-auth-login-url";
import { sendCheckoutUiBeacon } from "@/modules/handshake/lib/nr-beacon";
import { logInfo, logWarn } from "@/services/logging/logger";
import { RIVER_EVENT, flushRiver, trackRiver } from "@/services/river";

/** CheckoutUiEvents names. Keep in sync with the funnel dashboard. */
export const REAUTH_EVENTS = {
  REDIRECTED: "CHECKOUT_REAUTH_REDIRECTED",
  RECOVERED: "CHECKOUT_REAUTH_RECOVERED",
  FAILED: "CHECKOUT_REAUTH_FAILED",
  NATIVE_RELOADED: "CHECKOUT_REAUTH_NATIVE_RELOADED",
} as const;

/** The current URL plus the loop-guard marker, with every in-store param kept. */
function currentUrlWithReauthMarker(): string {
  const url = new URL(window.location.href);
  url.searchParams.set(REAUTH_MARKER_PARAM, REAUTH_MARKER_VALUE);
  return url.toString();
}

function readAndClearMarker(): boolean {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(REAUTH_MARKER_PARAM) !== REAUTH_MARKER_VALUE) {
      return false;
    }
    url.searchParams.delete(REAUTH_MARKER_PARAM);
    window.history.replaceState(window.history.state, "", url.toString());
    return true;
  } catch {
    return false;
  }
}

// Captured once at module load, before the router can rewrite the URL. The
// marker is stripped so a manual reload starts a fresh (single) attempt.
let returnedFromReauth = readAndClearMarker();

/** Test hook: re-read the marker from the current URL. */
export function resetReauthStateForTests(): void {
  returnedFromReauth = readAndClearMarker();
}

export type ReauthDecision = "redirected" | "show_error";

/**
 * Handles a 401 on the session fetch. Either navigates away — to Auth login on
 * the web, or a one-time reload inside the Tamara app — and returns
 * "redirected" (the caller should keep showing its loader), or returns
 * "show_error" when recovery isn't possible or already failed once.
 */
export function handleSessionUnauthorized(
  // replace, not assign: the 401 page has nothing to go back to, and keeping it
  // in history would bounce a Back press from Auth straight back into login.
  navigate: (url: string) => void = (url) => window.location.replace(url),
): ReauthDecision {
  const checkoutId = getResolvedCheckoutId();

  if (returnedFromReauth) {
    logWarn("checkout-session", "Session fetch returned 401 after re-login");
    sendCheckoutUiBeacon(REAUTH_EVENTS.FAILED, {
      checkout_id: checkoutId ?? "",
    });
    return "show_error";
  }

  if (isMobileBridgeAvailable()) {
    logWarn(
      "checkout-session",
      "Session fetch returned 401 in the app; reloading once",
    );
    sendCheckoutUiBeacon(REAUTH_EVENTS.NATIVE_RELOADED, {
      checkout_id: checkoutId ?? "",
    });
    navigate(currentUrlWithReauthMarker());
    return "redirected";
  }

  if (!isValidCheckoutId(checkoutId)) {
    logWarn(
      "checkout-session",
      "Session fetch returned 401; re-login not possible",
      { reason: "no_checkout_id" },
    );
    return "show_error";
  }

  logWarn(
    "checkout-session",
    "Session fetch returned 401; redirecting to login",
  );
  sendCheckoutUiBeacon(REAUTH_EVENTS.REDIRECTED, { checkout_id: checkoutId });
  trackRiver({ event: RIVER_EVENT.authRedirectTriggered, checkoutId });
  // Hard navigation below; without a keepalive flush the batcher's queue dies
  // with the page.
  flushRiver({ keepalive: true });
  navigate(
    buildAuthLoginUrl({
      checkoutId,
      search: window.location.search,
      markReauth: true,
    }),
  );
  return "redirected";
}

/** Call once the session fetch succeeds; reports a successful re-login. */
export function reportSessionLoaded(): void {
  if (!returnedFromReauth) return;
  returnedFromReauth = false;
  const checkoutId = getResolvedCheckoutId() ?? "";
  logInfo("checkout-session", "Session loaded after re-login");
  sendCheckoutUiBeacon(REAUTH_EVENTS.RECOVERED, { checkout_id: checkoutId });
}
